import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Maximize2, Minimize2, ChevronRight, FolderPlus, Plus, ChevronDown, ChevronUp, Folder, Pencil, Trash2, UserPlus, GripVertical, MoveHorizontal, UploadCloud, FileText, AlertTriangle, ExternalLink, Sparkles } from "lucide-react";
import AssessmentsPanel from "./AssessmentsPanel";
import { useAuth } from "@/context/AuthContext";
import Concept2AssignDialog from "./Concept2AssignDialog";

/** ── 2-letter TYPE_CODE + colour palette taken verbatim from the
 *   concept HTML (typeColor + TYPE_CODE dictionaries at line ~540). */
const TYPE_META = {
    video:   { code: "VI", color: "#2EB4AC", label: "Video" },
    article: { code: "AR", color: "#E7A33E", label: "Article" },
    doc:     { code: "DC", color: "#5B7FDE", label: "Doc" },
    course:  { code: "CR", color: "#C86B85", label: "Course" },
    podcast: { code: "PO", color: "#7FA65C", label: "Podcast" },
    quiz:    { code: "QZ", color: "#C6543F", label: "Quiz" },
    book:    { code: "BK", color: "#38B26D", label: "Book" },
    file:    { code: "FL", color: "#6B7280", label: "File" },
};
const typeMeta = (r) => TYPE_META[(r.type || "").toLowerCase()] || TYPE_META.file;
const metaLine = (r) => {
    const m = typeMeta(r);
    const bits = [m.label];
    if (r.level) bits.push(r.level[0].toUpperCase() + r.level.slice(1).toLowerCase());
    if (r.duration_minutes) bits.push(`${r.duration_minutes}m`);
    return bits.join(" · ");
};

/* ── inline "quick create" dialog for folder/resource — modeled on
 * concept's .form-overlay/.form-box (line ~287 in concept HTML). Also
 * used for EDIT (pass `initial` = existing row) — the fields pre-fill
 * and the submit label switches to "Save changes". */
const QuickForm = ({ title, fields, onCancel, onSubmit, submitLabel = "Create", initial = null, wide = false }) => {
    const [values, setValues] = useState(() =>
        Object.fromEntries(fields.map((f) => [f.key, initial?.[f.key] ?? f.default ?? ""]))
    );
    const [busy, setBusy] = useState(false);
    /* Per-field "uploading…" spinner state so multiple file fields can
     * live in the same form without stepping on each other. */
    const [uploadingKey, setUploadingKey] = useState(null);
    const uploadFile = async (key, file) => {
        if (!file) return;
        setUploadingKey(key);
        try {
            const form = new FormData();
            form.append("file", file);
            const r = await api.post("/learning/upload", form);
            setValues((v) => ({
                ...v,
                [key]: {
                    storage_path: r.data.storage_path,
                    file_original_name: r.data.original_filename,
                    content_type: r.data.content_type,
                    size_bytes: r.data.size_bytes,
                },
            }));
            toast.success(`Uploaded ${r.data.original_filename}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setUploadingKey(null); }
    };
    const clearFile = (key) => setValues((v) => ({ ...v, [key]: "" }));
    const submit = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const result = await onSubmit(values);
            // Contract: onSubmit resolves normally on success, throws on
            // failure. Auto-close on success guarantees the form never
            // gets wedged in the "Saving…" state even if the parent's
            // close-signal state update is batched or delayed.
            if (result !== false) onCancel?.();
        } catch (e) {
            /* onSubmit already toasted; leave form open so user can retry */
        } finally { setBusy(false); }
    };
    /* Enter anywhere in the form saves; Escape cancels. Matches native
     * form UX + user's Feb 2026 request. */
    const onKey = (e) => {
        if (e.key === "Enter" && !busy && e.target.tagName !== "TEXTAREA") {
            e.preventDefault();
            submit();
        } else if (e.key === "Escape") {
            e.preventDefault();
            onCancel?.();
        }
    };
    return (
        <div className="lh-c2-form-overlay" onClick={onCancel} data-testid="lh-c2-quickform">
            <div className={`lh-c2-form-box ${wide ? "lh-c2-form-box-wide" : ""}`} onClick={(e) => e.stopPropagation()} onKeyDown={onKey}>
                <h4>{title}</h4>
                <div className={wide ? "lh-c2-form-grid" : ""}>
                {fields.map((f) => (
                    <div className={`lh-c2-form-field ${f.col === "full" ? "lh-c2-form-field-full" : ""}`} key={f.key}>
                        <label>{f.label}</label>
                        {f.type === "textarea" ? (
                            <textarea
                                rows={f.rows || 3}
                                value={values[f.key]}
                                placeholder={f.placeholder}
                                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                                autoFocus={f.autoFocus}
                                data-testid={`lh-c2-qf-${f.key}`}
                            />
                        ) : f.type === "select" ? (
                            <select value={values[f.key]} onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} data-testid={`lh-c2-qf-${f.key}`}>
                                {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        ) : f.type === "file" ? (
                            <div className="lh-c2-qf-file" data-testid={`lh-c2-qf-${f.key}-wrap`}>
                                {values[f.key] && typeof values[f.key] === "object" && values[f.key].storage_path ? (
                                    <div className="lh-c2-qf-file-pill" data-testid={`lh-c2-qf-${f.key}-pill`}>
                                        <FileText size={14} />
                                        <span className="lh-c2-qf-file-name">{values[f.key].file_original_name}</span>
                                        <button
                                            type="button"
                                            className="lh-c2-qf-file-x"
                                            onClick={() => clearFile(f.key)}
                                            aria-label="Remove file"
                                            data-testid={`lh-c2-qf-${f.key}-clear`}
                                        ><X size={13} /></button>
                                    </div>
                                ) : (
                                    <label className="lh-c2-qf-file-drop" data-testid={`lh-c2-qf-${f.key}-drop`}>
                                        <UploadCloud size={16} />
                                        <span>{uploadingKey === f.key ? "Uploading…" : (f.placeholder || "Choose any file — PDF, DOC, PPT, image…")}</span>
                                        <input
                                            type="file"
                                            hidden
                                            onChange={(e) => uploadFile(f.key, e.target.files?.[0])}
                                            data-testid={`lh-c2-qf-${f.key}`}
                                        />
                                    </label>
                                )}
                            </div>
                        ) : (
                            <input
                                type={f.type || "text"}
                                value={values[f.key]}
                                placeholder={f.placeholder}
                                onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                                autoFocus={f.autoFocus}
                                data-testid={`lh-c2-qf-${f.key}`}
                            />
                        )}
                    </div>
                ))}
                </div>
                <div className="lh-c2-form-actions">
                    <button className="lh-c2-btn" onClick={onCancel} disabled={busy} data-testid="lh-c2-qf-cancel">Cancel</button>
                    <button className="lh-c2-btn lh-c2-btn-solid" onClick={submit} disabled={busy} data-testid="lh-c2-qf-submit">
                        {busy ? "Saving…" : submitLabel}
                    </button>
                </div>
            </div>
        </div>
    );
};

/** Concept-2 drill-down modal — mockup-parity: dept-tinted cover strip
 *  → title block (eyebrow + Lora heading + description + stats +
 *  toolbar) → folder / resource body with concept-style pill chips
 *  (24×24, TYPE_CODE letters, mono font). */
export const Concept2Modal = ({ collection, onClose, onOpenResource, onChanged, autoCovers, showCover, onEditCollection, onDeleteCollection, onRerollCover, canManage = false }) => {
    const [resources, setResources] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openFolders, setOpenFolders] = useState({});
    const [fullpage, setFullpage] = useState(false);
    const [addFolderParent, setAddFolderParent] = useState(undefined); // undefined = closed, null = root, "id" = under folder
    // Tab switcher inside the modal — "content" is the existing
    // folders+resources tree, "assessments" is the new quizzes/tests
    // builder + runner powered by AssessmentsPanel.
    const [activeTab, setActiveTab] = useState("content");
    const { user: currentUser } = useAuth();
    const [addResourceParent, setAddResourceParent] = useState(undefined);
    const [editingFolder, setEditingFolder] = useState(null);
    const [editingResource, setEditingResource] = useState(null);
    const [assignTarget, setAssignTarget] = useState(null); // { kind, id, name, assigned_to }
    const [movingResource, setMovingResource] = useState(null); // resource being moved
    const [moveTargetCid, setMoveTargetCid] = useState("");
    const [moveTargets, setMoveTargets] = useState([]);
    // Drag state — one live at a time. `dragRef` holds { kind, id, parentKey }
    // so the drop target can reject cross-parent drags cleanly.
    const [dragOverId, setDragOverId] = useState(null);
    const [dragOverSide, setDragOverSide] = useState(null); // "before" | "after"
    const dragRef = React.useRef(null);

    const load = async () => {
        try {
            const [rr, ff] = await Promise.all([
                api.get(`/learning/collections/${collection.id}/resources`),
                api.get(`/learning/collections/${collection.id}/folders`),
            ]);
            setResources(rr.data || []);
            setFolders(ff.data || []);
            if ((ff.data || []).length) setOpenFolders({ [ff.data[0].id]: true });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load collection");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [collection.id]);

    const totalMinutes = useMemo(() => resources.reduce((n, r) => n + (r.duration_minutes || 0), 0), [resources]);
    const rootRes = resources.filter((r) => !r.folder_id);
    const foldersByParent = {};
    folders.forEach((f) => {
        const p = f.parent_folder_id || null;
        (foldersByParent[p] = foldersByParent[p] || []).push(f);
    });

    /** Dept-tinted cover — matches concept: gradient of dept.color if
     *  no cover_image is present, otherwise centred cover_image. Falls
     *  back to concept's default teal → navy. */
    // Cover only renders when the collection actually has one AND either
    // auto-covers are enabled org-wide or the user just generated a fresh
    // cover this session (`showCover` override).
    const effectiveShow = !!collection.cover_image && (autoCovers || showCover);
    const coverStyle = effectiveShow
        ? { backgroundImage: `url(${collection.cover_image})`, backgroundSize: "cover", backgroundPosition: "center" }
        : (() => {
            const c = collection.cover_color || "#2EB4AC";
            return { background: `linear-gradient(150deg, ${c}, ${c}CC)` };
        })();

    const submitFolder = async ({ name }) => {
        const n = (name || "").trim();
        if (!n) { toast.error("Name is required"); throw new Error("validation"); }
        try {
            const payload = { name: n };
            if (addFolderParent) payload.parent_folder_id = addFolderParent;
            const r = await api.post(`/learning/collections/${collection.id}/folders`, payload);
            setFolders((prev) => [...prev, r.data]);
            if (addFolderParent) setOpenFolders((s) => ({ ...s, [addFolderParent]: true }));
            setAddFolderParent(undefined);
            toast.success(`Folder “${n}” created`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't create folder");
            throw e;
        }
    };

    const submitResource = async ({ name, type, url, duration_minutes, level, overview, learning_outcomes, notes, file }) => {
        const n = (name || "").trim();
        if (!n) { toast.error("Name is required"); throw new Error("validation"); }
        try {
            const payload = {
                name: n,
                type: type || "article",
                url: url || null,
                duration_minutes: duration_minutes ? Number(duration_minutes) : null,
                level: level || "beginner",
                folder_id: addResourceParent || null,
                overview: overview || "",
                learning_outcomes: (learning_outcomes || "").split("\n").map((s) => s.trim()).filter(Boolean),
                notes: notes || "",
            };
            if (file && typeof file === "object" && file.storage_path) {
                payload.storage_path = file.storage_path;
                payload.file_original_name = file.file_original_name;
                payload.content_type = file.content_type;
                payload.size_bytes = file.size_bytes;
            }
            const r = await api.post(`/learning/collections/${collection.id}/resources`, payload);
            setResources((prev) => [...prev, r.data]);
            if (addResourceParent) setOpenFolders((s) => ({ ...s, [addResourceParent]: true }));
            setAddResourceParent(undefined);
            toast.success(`Resource “${n}” added`);
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't add resource");
            throw e;
        }
    };

    /* ── Edit / Delete handlers (soft-delete on the server; the item
     *  disappears from list but lives 30 days in the Recycle bin). */
    const submitEditFolder = async ({ name }) => {
        const n = (name || "").trim();
        if (!n) { toast.error("Name is required"); throw new Error("validation"); }
        try {
            await api.patch(`/learning/learning-folders/${editingFolder.id}`, { name: n });
            setFolders((prev) => prev.map((f) => (f.id === editingFolder.id ? { ...f, name: n } : f)));
            setEditingFolder(null);
            toast.success("Folder renamed");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Rename failed");
            throw e;
        }
    };
    const submitEditResource = async ({ name, type, url, duration_minutes, level, overview, learning_outcomes, notes, file }) => {
        const n = (name || "").trim();
        if (!n) { toast.error("Name is required"); return; }
        try {
            const payload = {
                name: n,
                type: (type || "").toLowerCase() || "article",
                url: url || "",
                duration_minutes: duration_minutes ? Number(duration_minutes) : null,
                level: (level || "").toLowerCase() || "beginner",
                overview: overview ?? "",
                learning_outcomes: (learning_outcomes || "").split("\n").map((s) => s.trim()).filter(Boolean),
                notes: notes ?? "",
            };
            /* File field can be: object (new upload), string (unchanged /
             * already attached — do nothing), or "" (user cleared it → detach). */
            if (file && typeof file === "object" && file.storage_path) {
                payload.storage_path = file.storage_path;
                payload.file_original_name = file.file_original_name;
                payload.content_type = file.content_type;
                payload.size_bytes = file.size_bytes;
            } else if (file === "" && editingResource.storage_path) {
                payload.storage_path = "";  // detach
            }
            const r = await api.put(`/learning/resources/${editingResource.id}`, payload);
            setResources((prev) => prev.map((x) => (x.id === editingResource.id ? { ...x, ...r.data } : x)));
            setEditingResource(null);
            toast.success("Resource updated");
            onChanged?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
            throw e;
        }
    };
    const deleteFolder = async (folder) => {
        if (!window.confirm(`Move folder “${folder.name}” to the Recycle bin? Everything inside will move too. You can restore within 30 days.`)) return;
        try {
            await api.delete(`/learning/learning-folders/${folder.id}`);
            setFolders((prev) => prev.filter((f) => f.id !== folder.id));
            // remove resources under this folder locally so the UI updates
            setResources((prev) => prev.filter((r) => r.folder_id !== folder.id));
            toast.success("Folder moved to Recycle bin");
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };
    const deleteResource = async (resource) => {
        if (!window.confirm(`Move “${resource.name}” to the Recycle bin? You can restore within 30 days.`)) return;
        try {
            await api.delete(`/learning/resources/${resource.id}`);
            setResources((prev) => prev.filter((r) => r.id !== resource.id));
            toast.success("Resource moved to Recycle bin");
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    /* Move-to-collection — fetches every collection in the current
     * collection's department, minus the current one. Anyone in the
     * dept can be a target. Uses the existing POST /resources/{id}/move
     * endpoint. */
    const startMoveResource = async (resource) => {
        setMovingResource(resource);
        setMoveTargetCid("");
        try {
            const r = await api.get("/learning/collections");
            const dept = collection.owner_department_id;
            setMoveTargets((r.data || []).filter((c) => c.id !== collection.id && (!dept || c.owner_department_id === dept)));
        } catch { setMoveTargets([]); }
    };
    const confirmMoveResource = async () => {
        if (!movingResource || !moveTargetCid) return;
        try {
            await api.post(`/learning/resources/${movingResource.id}/move`, { collection_id: moveTargetCid });
            setResources((prev) => prev.filter((r) => r.id !== movingResource.id));
            toast.success(`Moved “${movingResource.name}” to its new home`);
            setMovingResource(null);
            setMoveTargetCid("");
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Move failed"); }
    };

    /* ── Reorder helpers — same-parent only (backend endpoints scope by
     *  `parent_folder_id` for folders and by `collection_id` for
     *  resources within/outside folders). */
    const reorderResources = async (folderId, orderedIds) => {
        // Optimistic: reshuffle the local list so the row visibly jumps to
        // its new slot on the same tick, then persist. `foldersByParent`
        // and `rootRes` are recomputed from array order, so this must
        // physically reorder the array — not just tweak the `order` field.
        setResources((prev) => {
            const scoped = prev.filter((r) => (r.folder_id || null) === (folderId || null));
            const others = prev.filter((r) => (r.folder_id || null) !== (folderId || null));
            const byId = new Map(scoped.map((r) => [r.id, r]));
            const reordered = orderedIds.map((id) => byId.get(id)).filter(Boolean).map((r, i) => ({ ...r, order: i }));
            return [...others, ...reordered];
        });
        try {
            await api.put(`/learning/collections/${collection.id}/resources/reorder`, { resource_ids: orderedIds });
        } catch (e) { toast.error(e?.response?.data?.detail || "Reorder failed"); await load(); }
    };
    const reorderFolders = async (parentId, orderedIds) => {
        setFolders((prev) => {
            const scoped = prev.filter((f) => (f.parent_folder_id || null) === (parentId || null));
            const others = prev.filter((f) => (f.parent_folder_id || null) !== (parentId || null));
            const byId = new Map(scoped.map((f) => [f.id, f]));
            const reordered = orderedIds.map((id) => byId.get(id)).filter(Boolean).map((f, i) => ({ ...f, order: i }));
            return [...others, ...reordered];
        });
        try {
            await api.put(`/learning/collections/${collection.id}/folders/reorder`, {
                folder_ids: orderedIds,
                parent_folder_id: parentId || null,
            });
        } catch (e) { toast.error(e?.response?.data?.detail || "Reorder failed"); await load(); }
    };
    const startDrag = (kind, id, parentKey) => (e) => {
        dragRef.current = { kind, id, parentKey };
        try { e.dataTransfer.effectAllowed = "move"; } catch { /* Safari */ }
    };
    const overRow = (kind, id, parentKey) => (e) => {
        const d = dragRef.current;
        if (!d || d.kind !== kind || d.parentKey !== parentKey) return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const side = e.clientY < midY ? "before" : "after";
        if (dragOverId !== id || dragOverSide !== side) {
            setDragOverId(id);
            setDragOverSide(side);
        }
    };
    const dropRow = (kind, targetId, parentKey, siblings) => (e) => {
        e.preventDefault();
        const d = dragRef.current;
        const side = dragOverSide;
        dragRef.current = null;
        setDragOverId(null);
        setDragOverSide(null);
        if (!d || d.kind !== kind || d.parentKey !== parentKey || d.id === targetId) return;
        const ids = siblings.map((s) => s.id);
        const fromIdx = ids.indexOf(d.id);
        let toIdx = ids.indexOf(targetId);
        if (fromIdx < 0 || toIdx < 0) return;
        ids.splice(fromIdx, 1);
        // Recompute target position after removal + apply side offset so the
        // dropped item lands exactly where the drop-line indicator showed.
        toIdx = ids.indexOf(targetId);
        const insertIdx = side === "after" ? toIdx + 1 : toIdx;
        ids.splice(insertIdx, 0, d.id);
        if (kind === "resource") reorderResources(parentKey || null, ids);
        else reorderFolders(parentKey || null, ids);
    };
    // Arrow-button reorder — moves a single row up (delta=-1) or down
    // (delta=+1) among its siblings and persists the new order. Same
    // endpoints as drag; guaranteed to work on touch / keyboard / any
    // browser where HTML5 drag misbehaves.
    const moveWithinSiblings = (kind, id, parentKey, siblings, delta) => {
        const ids = siblings.map((s) => s.id);
        const from = ids.indexOf(id);
        if (from === -1) return;
        const to = from + delta;
        if (to < 0 || to > ids.length - 1) return;
        ids.splice(from, 1);
        ids.splice(to, 0, id);
        if (kind === "resource") reorderResources(parentKey || null, ids);
        else reorderFolders(parentKey || null, ids);
    };

    const renderResource = (r, siblings, parentKey) => {
        const m = typeMeta(r);
        const isOver = dragOverId === r.id;
        const dropSide = isOver ? dragOverSide : null;
        const idx = siblings.findIndex((s) => s.id === r.id);
        const canUp = idx > 0;
        const canDown = idx >= 0 && idx < siblings.length - 1;
        // Drag lives on the grip only — the row's onClick keeps opening
        // the viewer; you must grab the grip to reorder.
        const rowDragProps = {
            onDragOver: overRow("resource", r.id, parentKey || null),
            onDragLeave: () => { if (dragOverId === r.id) { setDragOverId(null); setDragOverSide(null); } },
            onDrop: dropRow("resource", r.id, parentKey || null, siblings),
        };
        return (
            <div
                key={r.id}
                className={`lh-c2m-resource ${isOver ? "is-drop-target" : ""} ${dropSide ? `drop-${dropSide}` : ""}`}
                onClick={() => onOpenResource(r, collection)}
                data-testid={`lh-c2-mod-res-${r.id}`}
                {...rowDragProps}
            >
                <span
                    className="lh-c2m-drag"
                    title="Drag to reorder"
                    draggable
                    onDragStart={startDrag("resource", r.id, parentKey || null)}
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`lh-c2-mod-res-drag-${r.id}`}
                ><GripVertical size={12} /></span>
                <span className="lh-c2m-move-arrows" onClick={(e) => e.stopPropagation()}>
                    <button
                        type="button"
                        className="lh-c2m-move-btn"
                        disabled={!canUp}
                        title="Move up"
                        aria-label="Move up"
                        onClick={(e) => { e.stopPropagation(); moveWithinSiblings("resource", r.id, parentKey || null, siblings, -1); }}
                        data-testid={`lh-c2-mod-res-up-${r.id}`}
                    ><ChevronUp size={11} /></button>
                    <button
                        type="button"
                        className="lh-c2m-move-btn"
                        disabled={!canDown}
                        title="Move down"
                        aria-label="Move down"
                        onClick={(e) => { e.stopPropagation(); moveWithinSiblings("resource", r.id, parentKey || null, siblings, +1); }}
                        data-testid={`lh-c2-mod-res-down-${r.id}`}
                    ><ChevronDown size={11} /></button>
                </span>
                <div className="lh-c2m-chev-slot"><ChevronRight size={13} /></div>
                <div className="lh-c2m-pill" style={{ background: m.color }}>{m.code}</div>
                <div className="lh-c2m-res-body">
                    <h5>
                        {r.name}
                        {r.storage_missing && (
                            <span className="lh-c2-missing-pill" data-testid={`lh-c2-missing-res-${r.id}`} title="This file is missing from storage — re-upload it via Edit.">
                                <AlertTriangle size={10} /> File missing — re-upload
                            </span>
                        )}
                    </h5>
                </div>
                <div className="lh-c2m-res-meta">{metaLine(r)}</div>
                <div className="lh-c2m-row-actions">
                    <button
                        className="lh-c2m-iconbtn"
                        title="Assign to people"
                        onClick={(e) => { e.stopPropagation(); setAssignTarget({ kind: "resource", id: r.id, name: r.name, assigned_to: r.assigned_to || [] }); }}
                        data-testid={`lh-c2-mod-assign-res-${r.id}`}
                    ><UserPlus size={12} /></button>
                    <button
                        className="lh-c2m-iconbtn"
                        title="Move to another collection"
                        onClick={(e) => { e.stopPropagation(); startMoveResource(r); }}
                        data-testid={`lh-c2-mod-move-res-${r.id}`}
                    ><MoveHorizontal size={12} /></button>
                    <button
                        className="lh-c2m-iconbtn"
                        title="Edit resource"
                        onClick={(e) => { e.stopPropagation(); setEditingResource(r); }}
                        data-testid={`lh-c2-mod-edit-res-${r.id}`}
                    ><Pencil size={12} /></button>
                    <button
                        className="lh-c2m-iconbtn danger"
                        title="Delete resource"
                        onClick={(e) => { e.stopPropagation(); deleteResource(r); }}
                        data-testid={`lh-c2-mod-del-res-${r.id}`}
                    ><Trash2 size={12} /></button>
                </div>
            </div>
        );
    };

    const renderFolderTree = (parentId, depth = 0) => {
        const kids = foldersByParent[parentId] || [];
        return kids.map((f, kidIdx) => {
            const inside = resources.filter((r) => r.folder_id === f.id);
            const isOpen = !!openFolders[f.id];
            const isOver = dragOverId === f.id;
            const dropSide = isOver ? dragOverSide : null;
            const canUp = kidIdx > 0;
            const canDown = kidIdx < kids.length - 1;
            return (
                <div
                    key={f.id}
                    className={`lh-c2m-folder-block ${isOver ? "is-drop-target" : ""} ${dropSide ? `drop-${dropSide}` : ""}`}
                    style={{ marginLeft: depth * 18 }}
                    onDragOver={overRow("folder", f.id, parentId || null)}
                    onDragLeave={() => { if (dragOverId === f.id) { setDragOverId(null); setDragOverSide(null); } }}
                    onDrop={dropRow("folder", f.id, parentId || null, kids)}
                >
                    <div className="lh-c2m-folder-head">
                        <span
                            className="lh-c2m-drag"
                            title="Drag to reorder"
                            draggable
                            onDragStart={startDrag("folder", f.id, parentId || null)}
                            data-testid={`lh-c2-mod-folder-drag-${f.id}`}
                        ><GripVertical size={12} /></span>
                        <span className="lh-c2m-move-arrows" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="lh-c2m-move-btn"
                                disabled={!canUp}
                                title="Move up"
                                aria-label="Move up"
                                onClick={(e) => { e.stopPropagation(); moveWithinSiblings("folder", f.id, parentId || null, kids, -1); }}
                                data-testid={`lh-c2-mod-folder-up-${f.id}`}
                            ><ChevronUp size={11} /></button>
                            <button
                                type="button"
                                className="lh-c2m-move-btn"
                                disabled={!canDown}
                                title="Move down"
                                aria-label="Move down"
                                onClick={(e) => { e.stopPropagation(); moveWithinSiblings("folder", f.id, parentId || null, kids, +1); }}
                                data-testid={`lh-c2-mod-folder-down-${f.id}`}
                            ><ChevronDown size={11} /></button>
                        </span>
                        <button
                            className="lh-c2m-folder-name-btn"
                            onClick={() => setOpenFolders((s) => ({ ...s, [f.id]: !s[f.id] }))}
                            data-testid={`lh-c2-mod-folder-${f.id}`}
                        >
                            <ChevronDown size={11} className={`lh-c2m-folder-chev ${isOpen ? "" : "collapsed"}`} />
                            <Folder size={12} strokeWidth={2} className="lh-c2m-folder-ic" />
                            <span className="lh-c2m-folder-name">{f.name}</span>
                            {(() => {
                                // Total resource count = direct resources +
                                // every resource inside any descendant folder.
                                const descendantIds = new Set([f.id]);
                                let grew = true;
                                while (grew) {
                                    grew = false;
                                    for (const g of folders) {
                                        if (g.parent_folder_id && descendantIds.has(g.parent_folder_id) && !descendantIds.has(g.id)) {
                                            descendantIds.add(g.id); grew = true;
                                        }
                                    }
                                }
                                const count = resources.filter((r) => r.folder_id && descendantIds.has(r.folder_id)).length;
                                return <span className="lh-c2m-folder-count" data-testid={`lh-c2-mod-folder-count-${f.id}`}>{count}</span>;
                            })()}
                        </button>
                        <span className="lh-c2m-folder-actions">
                            <button
                                className="lh-c2m-iconbtn"
                                title="Add subfolder"
                                onClick={(e) => { e.stopPropagation(); setAddFolderParent(f.id); }}
                                data-testid={`lh-c2-mod-add-subfolder-${f.id}`}
                            ><FolderPlus size={12} /></button>
                            <button
                                className="lh-c2m-iconbtn"
                                title="Add resource here"
                                onClick={(e) => { e.stopPropagation(); setAddResourceParent(f.id); }}
                                data-testid={`lh-c2-mod-add-inner-res-${f.id}`}
                            ><Plus size={12} /></button>
                            <button
                                className="lh-c2m-iconbtn"
                                title="Rename folder"
                                onClick={(e) => { e.stopPropagation(); setEditingFolder(f); }}
                                data-testid={`lh-c2-mod-edit-folder-${f.id}`}
                            ><Pencil size={12} /></button>
                            <button
                                className="lh-c2m-iconbtn danger"
                                title="Delete folder"
                                onClick={(e) => { e.stopPropagation(); deleteFolder(f); }}
                                data-testid={`lh-c2-mod-del-folder-${f.id}`}
                            ><Trash2 size={12} /></button>
                        </span>
                    </div>
                    {isOpen && (
                        <div className="lh-c2m-folder-body">
                            {inside.map((r) => renderResource(r, inside, f.id))}
                            {renderFolderTree(f.id, depth + 1)}
                        </div>
                    )}
                </div>
            );
        });
    };

    return createPortal(
        <div className={`lh-c2-modal-backdrop ${fullpage ? "fullpage" : ""}`} onClick={onClose} data-testid="lh-c2-modal-backdrop">
            <div className={`lh-c2m ${fullpage ? "fullpage" : ""}`} onClick={(e) => e.stopPropagation()} data-testid={`lh-c2-modal-${collection.id}`}>
                {/* Gradient cover strip (dept-tinted, mockup-parity) */}
                <div className="lh-c2m-cover" style={coverStyle}>
                    {!effectiveShow && <div className="lh-c2m-cover-dots" />}
                    <div className="lh-c2m-cover-actions">
                        <button
                            className="lh-c2m-cover-btn"
                            onClick={() => window.open(`/learning?collection=${collection.id}`, "_blank", "noopener")}
                            aria-label="Open in a new tab"
                            title="Open in a new tab"
                            data-testid="lh-c2-mod-open-new"
                        ><ExternalLink size={13} /></button>
                        <button
                            className="lh-c2m-cover-btn"
                            onClick={() => setFullpage((v) => !v)}
                            aria-label={fullpage ? "Exit full page" : "Full page"}
                            title={fullpage ? "Exit full page" : "Full page"}
                            data-testid="lh-c2-mod-expand"
                        >{fullpage ? <Minimize2 size={13} /> : <Maximize2 size={13} />}</button>
                        <button className="lh-c2m-cover-btn" onClick={onClose} aria-label="Close" data-testid="lh-c2-modal-close"><X size={13} /></button>
                    </div>
                </div>

                {/* Title block */}
                <div className="lh-c2m-title-block">
                    <div className="lh-c2m-eyebrow">{(collection.owner_department_name || "Collection")}</div>
                    <h2 className="lh-c2m-title">{collection.name}</h2>
                    {collection.description && <p className="lh-c2m-desc">{collection.description}</p>}
                    <div className="lh-c2m-stats-inline">
                        <span><b>{folders.length}</b> folders</span>
                        <span><b>{resources.length}</b> resources</span>
                        {totalMinutes > 0 && <span><b>{totalMinutes}</b> min total</span>}
                    </div>
                    <div className="lh-c2m-toolbar">
                        <button
                            className="lh-c2m-tbtn"
                            onClick={() => setAddFolderParent(null)}
                            data-testid="lh-c2-mod-add-folder"
                        ><FolderPlus size={13} /> Add folder</button>
                        <button
                            className="lh-c2m-tbtn"
                            onClick={() => setAddResourceParent(null)}
                            data-testid="lh-c2-mod-add-resource"
                        ><Plus size={13} /> Add resource</button>
                        <button
                            className="lh-c2m-tbtn"
                            onClick={() => setAssignTarget({ kind: "collection", id: collection.id, name: collection.name, assigned_to: collection.assigned_to || [] })}
                            data-testid="lh-c2-mod-assign-coll"
                        ><UserPlus size={13} /> Assign</button>
                        {onRerollCover && (
                            <button
                                className="lh-c2m-tbtn"
                                onClick={() => onRerollCover(collection)}
                                title={collection.cover_image ? "Regenerate cover with Nano Banana" : "Generate cover with Nano Banana"}
                                data-testid="lh-c2-mod-reroll"
                            ><Sparkles size={13} /> Generate cover</button>
                        )}
                        {canManage && onEditCollection && (
                            <button
                                className="lh-c2m-tbtn"
                                onClick={() => onEditCollection(collection)}
                                data-testid="lh-c2-mod-edit-coll"
                            ><Pencil size={13} /> Edit</button>
                        )}
                        {canManage && onDeleteCollection && (
                            <button
                                className="lh-c2m-tbtn lh-c2m-tbtn-danger"
                                onClick={() => onDeleteCollection(collection)}
                                data-testid="lh-c2-mod-delete-coll"
                            ><Trash2 size={13} /> Delete</button>
                        )}
                    </div>
                </div>

                {/* Body */}
                <div className="lh-c2m-body">
                    <div className="lh-c2m-tabs" data-testid="lh-c2m-tabs">
                        <button
                            type="button"
                            className={`lh-c2m-tab ${activeTab === "content" ? "active" : ""}`}
                            onClick={() => setActiveTab("content")}
                            data-testid="lh-c2m-tab-content"
                        >Content</button>
                        <button
                            type="button"
                            className={`lh-c2m-tab ${activeTab === "assessments" ? "active" : ""}`}
                            onClick={() => setActiveTab("assessments")}
                            data-testid="lh-c2m-tab-assessments"
                        >Assessments</button>
                    </div>
                    {activeTab === "content" ? (
                        loading ? (
                            <div style={{ padding: 30, textAlign: "center", color: "#6B7280", fontSize: 13.5 }}>Loading…</div>
                        ) : (
                            <>
                                {rootRes.map((r) => renderResource(r, rootRes, null))}
                                {renderFolderTree(null)}
                                {resources.length === 0 && folders.length === 0 && (
                                    <div style={{ padding: 40, textAlign: "center", color: "#6B7280", fontSize: 14 }}>This collection is empty.</div>
                                )}
                            </>
                        )
                    ) : (
                        <AssessmentsPanel collection={collection} folders={folders} resources={resources} userId={currentUser?.user_id} />
                    )}
                </div>

                {addFolderParent !== undefined && (
                    <QuickForm
                        title={addFolderParent ? "Add subfolder" : "Add folder"}
                        fields={[
                            { key: "name", label: "Folder name", placeholder: "e.g. Onboarding basics", autoFocus: true },
                        ]}
                        submitLabel="Create folder"
                        onCancel={() => setAddFolderParent(undefined)}
                        onSubmit={submitFolder}
                    />
                )}
                {addResourceParent !== undefined && (
                    <QuickForm
                        wide
                        title={addResourceParent ? "Add resource to folder" : "Add resource"}
                        fields={[
                            { key: "name", label: "Resource title", placeholder: "e.g. AMS bidding strategy", autoFocus: true, col: "full" },
                            { key: "type", label: "Type", type: "select", default: "article", options: [
                                { value: "article", label: "Article" },
                                { value: "video", label: "Video" },
                                { value: "doc", label: "Doc" },
                                { value: "course", label: "Course" },
                                { value: "podcast", label: "Podcast" },
                                { value: "quiz", label: "Quiz" },
                                { value: "book", label: "Book" },
                            ] },
                            { key: "level", label: "Level", type: "select", default: "beginner", options: [
                                { value: "beginner", label: "Beginner" },
                                { value: "intermediate", label: "Intermediate" },
                                { value: "advanced", label: "Advanced" },
                            ] },
                            { key: "url", label: "Source URL (optional)", placeholder: "https://…" },
                            { key: "duration_minutes", label: "Duration (min, optional)", type: "number", placeholder: "5" },
                            { key: "file", label: "Or upload a file (any format — opens in a new tab)", type: "file", placeholder: "Choose a file — PDF, DOC, PPT, XLSX, image…", col: "full" },
                            { key: "overview", label: "Overview (optional)", type: "textarea", rows: 3, placeholder: "One-paragraph description of what this covers…", col: "full" },
                            { key: "learning_outcomes", label: "Learning outcomes (one per line, optional)", type: "textarea", rows: 3, placeholder: "Understand X\nApply Y\nExplain Z", col: "full" },
                            { key: "notes", label: "Instructor notes (optional)", type: "textarea", rows: 2, placeholder: "How to use this resource…", col: "full" },
                        ]}
                        submitLabel="Add resource"
                        onCancel={() => setAddResourceParent(undefined)}
                        onSubmit={submitResource}
                    />
                )}
                {editingFolder && (
                    <QuickForm
                        title="Rename folder"
                        initial={{ name: editingFolder.name }}
                        fields={[
                            { key: "name", label: "Folder name", placeholder: "Folder name", autoFocus: true },
                        ]}
                        submitLabel="Save changes"
                        onCancel={() => setEditingFolder(null)}
                        onSubmit={submitEditFolder}
                    />
                )}
                {editingResource && (
                    <QuickForm
                        wide
                        title="Edit resource"
                        initial={{
                            name: editingResource.name || "",
                            type: (editingResource.type || "article").toLowerCase(),
                            level: (editingResource.level || "beginner").toLowerCase(),
                            url: editingResource.url || "",
                            duration_minutes: editingResource.duration_minutes ?? "",
                            overview: editingResource.overview || "",
                            learning_outcomes: (editingResource.learning_outcomes || []).join("\n"),
                            notes: editingResource.notes || "",
                            file: editingResource.storage_path ? {
                                storage_path: editingResource.storage_path,
                                file_original_name: editingResource.file_original_name || editingResource.name,
                                content_type: editingResource.content_type || "",
                                size_bytes: editingResource.size_bytes || 0,
                            } : "",
                        }}
                        fields={[
                            { key: "name", label: "Resource title", placeholder: "e.g. AMS bidding strategy", autoFocus: true, col: "full" },
                            { key: "type", label: "Type", type: "select", default: "article", options: [
                                { value: "article", label: "Article" },
                                { value: "video", label: "Video" },
                                { value: "doc", label: "Doc" },
                                { value: "course", label: "Course" },
                                { value: "podcast", label: "Podcast" },
                                { value: "quiz", label: "Quiz" },
                                { value: "book", label: "Book" },
                                { value: "file", label: "File" },
                            ] },
                            { key: "level", label: "Level", type: "select", default: "beginner", options: [
                                { value: "beginner", label: "Beginner" },
                                { value: "intermediate", label: "Intermediate" },
                                { value: "advanced", label: "Advanced" },
                            ] },
                            { key: "url", label: "Source URL (optional)", placeholder: "https://…" },
                            { key: "duration_minutes", label: "Duration (min, optional)", type: "number", placeholder: "5" },
                            { key: "file", label: "Or upload a file (any format — opens in a new tab)", type: "file", placeholder: "Choose a file — PDF, DOC, PPT, XLSX, image…", col: "full" },
                            { key: "overview", label: "Overview (optional)", type: "textarea", rows: 3, placeholder: "One-paragraph description of what this covers…", col: "full" },
                            { key: "learning_outcomes", label: "Learning outcomes (one per line, optional)", type: "textarea", rows: 3, placeholder: "Understand X\nApply Y\nExplain Z", col: "full" },
                            { key: "notes", label: "Instructor notes (optional)", type: "textarea", rows: 2, placeholder: "How to use this resource…", col: "full" },
                        ]}
                        submitLabel="Save changes"
                        onCancel={() => setEditingResource(null)}
                        onSubmit={submitEditResource}
                    />
                )}
                {assignTarget && (
                    <Concept2AssignDialog
                        kind={assignTarget.kind}
                        itemId={assignTarget.id}
                        itemName={assignTarget.name}
                        initial={assignTarget.assigned_to}
                        onClose={() => setAssignTarget(null)}
                        onSaved={(ids) => {
                            if (assignTarget.kind === "resource") {
                                setResources((prev) => prev.map((x) => x.id === assignTarget.id ? { ...x, assigned_to: ids } : x));
                            }
                            setAssignTarget(null);
                            onChanged?.();
                        }}
                    />
                )}
                {movingResource && (
                    <div className="lh-c2-form-overlay" onClick={() => setMovingResource(null)} data-testid="lh-c2-move-overlay">
                        <div className="lh-c2-form-box" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => { if (e.key === "Enter" && moveTargetCid) { e.preventDefault(); confirmMoveResource(); } else if (e.key === "Escape") { setMovingResource(null); } }}>
                            <h4>Move resource</h4>
                            <p style={{ fontSize: 12.5, color: "var(--ink-soft)", margin: "-6px 0 12px" }}>
                                Pick a target collection in this department to move <b style={{ color: "var(--navy)" }}>{movingResource.name}</b>.
                            </p>
                            <div className="lh-c2-form-field">
                                <label>Target collection</label>
                                <select value={moveTargetCid} onChange={(e) => setMoveTargetCid(e.target.value)} data-testid="lh-c2-move-select" autoFocus>
                                    <option value="">Select a collection…</option>
                                    {moveTargets.map((c) => (
                                        <option key={c.id} value={c.id}>{c.name}{c.owner_department_name ? ` — ${c.owner_department_name}` : ""}</option>
                                    ))}
                                </select>
                                {moveTargets.length === 0 && (
                                    <p style={{ fontSize: 11.5, color: "var(--ink-soft)", marginTop: 6 }}>No other collections in this department.</p>
                                )}
                            </div>
                            <div className="lh-c2-form-actions">
                                <button className="lh-c2-btn" onClick={() => setMovingResource(null)} data-testid="lh-c2-move-cancel">Cancel</button>
                                <button className="lh-c2-btn lh-c2-btn-solid" onClick={confirmMoveResource} disabled={!moveTargetCid} data-testid="lh-c2-move-confirm">
                                    Move resource
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

export default Concept2Modal;
